import { useCallback, useEffect, useState } from 'react';
import { CheckCircle, Package, Truck, XCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { mktApi, errMsg } from '@/pages/marketing/marketingApi';
import { jd, yen } from '@/pages/marketing/mktShared';
import { useDepartment } from './DepartmentContext';
import { todayJst } from '@/utils/jstDate';

const SECTIONS = [
  { status: 'qc_pending', label: '検査待ち', cls: 'text-violet-700 bg-violet-50 border-violet-200' },
  { status: 'qc_passed', label: '検査合格', cls: 'text-teal-700 bg-teal-50 border-teal-200' },
  { status: 'ready_to_ship', label: '出荷準備完了', cls: 'text-cyan-700 bg-cyan-50 border-cyan-200' },
  { status: 'partially_shipped', label: '一部出荷（残数あり）', cls: 'text-amber-700 bg-amber-50 border-amber-200' },
];

export default function Shipping() {
  const { activeId, isAll } = useDepartment();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [failTarget, setFailTarget] = useState(null);
  const [failNote, setFailNote] = useState('');
  const [shipTarget, setShipTarget] = useState(null);
  const [ship, setShip] = useState({ shipped_at: '', carrier: '', tracking_no: '', note: '' });
  const [shipQty, setShipQty] = useState({}); // line_id → qty(string)

  const deptParam = activeId && !isAll ? activeId : null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setOrders(await mktApi.shippingQueue(deptParam ? { department_id: deptParam } : {}));
    } catch (e) { toast.error(errMsg(e, '出荷キューの取得に失敗しました')); }
    finally { setLoading(false); }
  }, [deptParam]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); await load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const remainingOf = (ln) => Math.max(0, Number(((ln?.qty) || 0) - ((ln?.shipped_qty) || 0)) || 0);

  const openShip = (o) => {
    setShip({ shipped_at: todayJst(), carrier: '', tracking_no: '', note: '' });
    setShipQty(Object.fromEntries((o.lines || []).map((ln) => [ln.line_id, String(remainingOf(ln))])));
    setShipTarget(o);
  };

  return (
    <div className="space-y-4" data-testid="mfg-shipping-page">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-zinc-900">出荷管理</h1>
        <p className="text-xs text-zinc-500 mt-0.5">営業受注の検査（QC）〜出荷を管理します。出荷すると在庫が引き落とされ、営業へ引き渡されます</p>
      </div>

      {loading && <p className="py-10 text-center text-xs text-zinc-400">読み込み中…</p>}
      {!loading && orders.length === 0 && (
        <div className="rounded-xl border border-dashed border-zinc-200 bg-white py-14 text-center text-sm text-zinc-400" data-testid="mfg-shipping-empty">
          検査・出荷待ちの営業受注はありません
        </div>
      )}

      {!loading && SECTIONS.map((sec) => {
        const list = orders.filter((o) => o.status === sec.status);
        if (!list.length) return null;
        return (
          <div key={sec.status} className="space-y-2">
            <div className="flex items-center gap-2">
              <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${sec.cls}`}>{sec.label}</span>
              <span className="text-[11px] text-zinc-400">{list.length}件</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {list.map((o) => (
                <div key={o.id} className="rounded-xl border border-zinc-200 bg-white p-4" data-testid={`shipping-card-${o.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-mono text-xs font-semibold text-fuchsia-700">{o.order_no}</div>
                      <div className="font-medium text-zinc-900 truncate">{o.title || '（件名なし）'}</div>
                      <div className="text-[11px] text-zinc-500">{o.customer_name} ・ 納期 {jd(o.due_date)} ・ {yen(o.total)}</div>
                    </div>
                  </div>
                  <ul className="mt-2 space-y-0.5 text-xs text-zinc-600">
                    {(o.lines || []).map((ln) => (
                      <li key={ln.line_id} className="flex items-center gap-2">
                        <Package size={12} className="text-zinc-300 shrink-0" />
                        <span className="truncate">{ln.name}</span>
                        <span className="ml-auto tabular-nums shrink-0">
                          {(ln.shipped_qty || 0) > 0
                            ? <><span className="text-emerald-600 font-semibold">{ln.shipped_qty}</span><span className="text-zinc-400">/</span>{ln.qty} {ln.uom}</>
                            : <>{ln.qty} {ln.uom}</>}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {o.fulfillment_track?.qc?.result === 'fail' && sec.status === 'qc_pending' && (
                    <p className="mt-2 rounded bg-red-50 border border-red-100 px-2 py-1 text-[11px] text-red-600">前回検査: 不合格{o.fulfillment_track.qc.note ? `（${o.fulfillment_track.qc.note}）` : ''}</p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2 border-t border-zinc-200 pt-2.5">
                    {sec.status === 'qc_pending' && (
                      <>
                        <Button size="sm" disabled={busy} onClick={() => run(() => mktApi.qc(o.id, { result: 'pass' }), '検査合格を記録しました')}
                          className="bg-teal-700 hover:bg-teal-800 h-8" data-testid={`shipping-qc-pass-${o.id}`}>
                          <CheckCircle size={14} className="mr-1" /> 検査合格
                        </Button>
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => { setFailNote(''); setFailTarget(o); }}
                          className="text-red-600 border-red-200 hover:bg-red-50 h-8" data-testid={`shipping-qc-fail-${o.id}`}>
                          <XCircle size={14} className="mr-1" /> 不合格
                        </Button>
                      </>
                    )}
                    {sec.status === 'qc_passed' && (
                      <Button size="sm" disabled={busy} onClick={() => run(() => mktApi.ready(o.id), '出荷準備完了にしました')}
                        className="bg-teal-700 hover:bg-teal-800 h-8" data-testid={`shipping-ready-${o.id}`}>
                        <Package size={14} className="mr-1" /> 出荷準備完了
                      </Button>
                    )}
                    {(sec.status === 'ready_to_ship' || sec.status === 'partially_shipped') && (
                      <Button size="sm" disabled={busy} onClick={() => openShip(o)}
                        className="bg-teal-700 hover:bg-teal-800 h-8" data-testid={`shipping-ship-${o.id}`}>
                        <Truck size={14} className="mr-1" /> {sec.status === 'partially_shipped' ? '残りを出荷' : '出荷する'}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* QC不合格 dialog */}
      <Dialog open={!!failTarget} onOpenChange={(v) => !v && setFailTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="shipping-qc-fail-dialog">
          <DialogHeader><DialogTitle>検査不合格 — {failTarget?.order_no}</DialogTitle></DialogHeader>
          <Textarea value={failNote} onChange={(e) => setFailNote(e.target.value)} placeholder="不合格理由" rows={2} data-testid="shipping-qc-fail-note" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setFailTarget(null)}>キャンセル</Button>
            <Button disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="shipping-qc-fail-confirm"
              onClick={() => { const t = failTarget; setFailTarget(null); run(() => mktApi.qc(t.id, { result: 'fail', note: failNote || null }), '検査不合格を記録しました'); }}>
              不合格を記録
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 出荷 dialog */}
      <Dialog open={!!shipTarget} onOpenChange={(v) => !v && setShipTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="shipping-ship-dialog">
          <DialogHeader><DialogTitle>出荷入力 — {shipTarget?.order_no}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-[11px] text-emerald-700">
              出荷した数量分の在庫が引き落とされます。全量出荷で営業の「売上確定待ち」へ、一部のみなら「一部出荷」として残数を管理します。
            </p>
            <div className="rounded-lg border border-zinc-200 overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50/70 text-left text-[10px] text-zinc-400">
                    <th className="px-2.5 py-1.5 font-medium">品名</th>
                    <th className="px-2 py-1.5 font-medium text-right">残数</th>
                    <th className="px-2.5 py-1.5 font-medium text-right">今回出荷数</th>
                  </tr>
                </thead>
                <tbody>
                  {(shipTarget?.lines || []).map((ln) => {
                    const rem = remainingOf(ln);
                    return (
                      <tr key={ln.line_id} className="border-b border-zinc-50 last:border-0">
                        <td className="px-2.5 py-1.5 text-zinc-700">{ln.name}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-zinc-500">{rem} {ln.uom}</td>
                        <td className="px-2.5 py-1.5 text-right">
                          <Input type="number" min="0" max={rem} step="any" value={shipQty[ln.line_id] ?? ''}
                            onChange={(e) => setShipQty((p) => ({ ...p, [ln.line_id]: e.target.value }))}
                            disabled={rem <= 0}
                            className="h-7 w-24 text-right ml-auto" data-testid={`shipping-ship-qty-${ln.line_id}`} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-zinc-600">出荷日</label>
                <Input type="date" value={ship.shipped_at} onChange={(e) => setShip((p) => ({ ...p, shipped_at: e.target.value }))} className="mt-1" data-testid="shipping-ship-date" />
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-600">運送会社</label>
                <Input value={ship.carrier} onChange={(e) => setShip((p) => ({ ...p, carrier: e.target.value }))} placeholder="例）ヤマト運輸" className="mt-1" data-testid="shipping-ship-carrier" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">送り状番号</label>
              <Input value={ship.tracking_no} onChange={(e) => setShip((p) => ({ ...p, tracking_no: e.target.value }))} className="mt-1" data-testid="shipping-ship-tracking" />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">備考</label>
              <Textarea value={ship.note} onChange={(e) => setShip((p) => ({ ...p, note: e.target.value }))} rows={2} className="mt-1" data-testid="shipping-ship-note" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShipTarget(null)}>キャンセル</Button>
              <Button disabled={busy} className="bg-teal-700 hover:bg-teal-800" data-testid="shipping-ship-confirm"
                onClick={() => {
                  const t = shipTarget;
                  const lines = (t.lines || []).map((ln) => ({ line_id: ln.line_id, qty: Number(shipQty[ln.line_id]) || 0 }));
                  if (!lines.some((l) => l.qty > 0)) { toast.error('出荷する数量を入力してください'); return; }
                  setShipTarget(null);
                  run(() => mktApi.ship(t.id, {
                    shipped_at: ship.shipped_at ? new Date(ship.shipped_at).toISOString() : null,
                    carrier: ship.carrier || null, tracking_no: ship.tracking_no || null,
                    note: ship.note || null, lines,
                  }), '出荷を記録しました（在庫引落し済）');
                }}>
                <Truck size={14} className="mr-1" /> 出荷を確定
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
